import { Button, Stack } from '@form-factory/chatbot-ui';
import { efAuthSchema, withClubId } from '@form-factory/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { Form, Input, type TypedFormState } from '@utima/ui-informed';
import { memo } from 'react';
import type { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { useEfErrorHandler } from '@/hooks/useEfErrorHandler';
import { aiCoreApi } from '@/services/aiCoreClient';
import { useBoundStore } from '@/store/store';

import { EfErrorAlert } from '../../components/alerts/EfErrorAlert.js';
import { ClubSelect } from '../../components/eFitness/ClubSelect.js';

const signInSchema = withClubId(efAuthSchema);
type SignInFormValues = z.infer<typeof signInSchema>;

export const SignInRoute = memo(function SignIn() {
  const { errors, handleError, resetErrors } = useEfErrorHandler();
  const { t } = useLingui();
  const { pushRoute, login } = useBoundStore(
    useShallow(state => ({
      pushRoute: state.pushRoute,
      login: state.login,
    })),
  );

  const routeToChat = () => {
    pushRoute('chat');
  };

  const signInMutation = useMutation({
    mutationFn: (values: SignInFormValues) => {
      return aiCoreApi.eFitness.auth.login({
        clubId: values.clubId,
        login: values.login,
        password: values.password,
      });
    },
  });

  const handleSubmit = async ({ values }: TypedFormState<SignInFormValues>) => {
    resetErrors();

    try {
      const { user, token, eFitness } =
        await signInMutation.mutateAsync(values);

      // Ensure user has clubId for downstream hooks (e.g., refresh)
      const userWithClubId = {
        ...(user as any),
        clubId: (user as any)?.clubId ?? values.clubId,
      } as typeof user;
      login(userWithClubId, token, eFitness);
      routeToChat();
    } catch (error) {
      await handleError(error);
    }
  };

  return (
    <Stack onBack={routeToChat} title={t`Login`}>
      <Form<SignInFormValues>
        zodSchema={signInSchema as never}
        className='flex h-full flex-col justify-center'
        onSubmit={handleSubmit}
      >
        <EfErrorAlert errors={errors} />
        <div className='flex flex-col gap-4'>
          <ClubSelect
            defaultValue='895'
            required
            name='clubId'
            label={t`Clubs`}
          />
          <Input required name='login' type='text' label={t`Email`} />
          <Input
            required
            name='password'
            type='password'
            passwordPreview
            label={t`Password`}
          />
          <Button
            type='submit'
            size='lg'
            disabled={signInMutation.isPending}
            loading={signInMutation.isPending}
          >
            <Trans>Sign in</Trans>
          </Button>
        </div>
      </Form>
    </Stack>
  );
});
